import { useCallback, useMemo, useState } from 'react'

import {
  listDepositActionParams,
  listLeadActionParams,
  listPayoutActionParams,
  listPayoutTransactionActionParams,
  listSalesAgentActionParams,
  listSalesAgentCountryActionParams,
  listScheduledActionParams,
  listTaskActionParams,
  listTradingAccountActionParams,
  listUserActionParams,
  listWithdrawalActionParams,
} from '@api/orval/apiHooks'
import { orvalFetch, type Any } from '@api/orvalFetcher'
import { extractFieldErrors, type FieldErrors } from '@components/DynamicActions/lib/fieldErrors'
import type { Action, ActionParams, DynamicActionsProps, FetchMethod } from '@components/DynamicActions/types'
import { handleCsvDownload } from '@lib/downloadCsv'
import { handlePdfDownload } from '@lib/downloadPdf'
import { emitToast } from '@lib/emitToast'

type Type = DynamicActionsProps['type']

const getFetcher = (type: Type) => {
  switch (type) {
    case 'trading_account':
      return listTradingAccountActionParams
    case 'payouts':
      return listPayoutActionParams
    case 'payout_transactions':
      return listPayoutTransactionActionParams as typeof listPayoutActionParams
    case 'scheduled_actions':
      return listScheduledActionParams
    case 'tasks':
      return listTaskActionParams
    case 'sales_agents':
      return listSalesAgentActionParams
    case 'sales_agent_countries':
      return listSalesAgentCountryActionParams
    case 'leads':
      return listLeadActionParams
    case 'deposits':
      return listDepositActionParams
    case 'withdrawals':
      return listWithdrawalActionParams
    // BE:SD-2737 — swap to a listQuestionnaireActionParams fetcher once IB actions
    // need require_params. Onboarding actions are confirm-only today, so the fetcher
    // is never called for this type; the default keeps the switch exhaustive.
    // Same for 'agreements': a future `require_params` agreement action would otherwise hit
    // /admin/api/users/{agreementId}/actions/new — add a fetcher before that lands.
    case 'questionnaire':
    case 'agreements':
    case 'user':
    default:
      return listUserActionParams
  }
}

/**
 * useActionParams
 *
 * Description:
 *  Caches and fetches action-specific parameters required to render
 *  action forms. Avoids refetching params already retrieved.
 *
 * Usage:
 *  const { actionParamsMap, fetchActionParams } = useActionParams(itemId, 'user')
 */
export function useActionParams(itemId: number | undefined | null, type: Type) {
  const [actionParamsMap, setActionParamsMap] = useState<Record<string, ActionParams>>({})

  const fetchActionParams = useCallback(
    async (action: Action) => {
      if (!itemId) return

      const fetcher = getFetcher(type)

      const { action_name, require_params } = action

      // Skip if has been requested before or params are not required
      if (!require_params || actionParamsMap[action_name]) {
        return actionParamsMap[action_name]
      }

      try {
        const data = await fetcher(itemId, { action_name })

        if (data) {
          setActionParamsMap((prev) => ({ ...prev, [action_name]: data as unknown as ActionParams }))
        }

        return data
      } catch (error) {
        console.error(`Failed to fetch params for action: "${action_name}"`, error)
      }
    },
    [itemId, type, actionParamsMap],
  )

  return useMemo(() => ({ actionParamsMap, fetchActionParams }), [actionParamsMap, fetchActionParams])
}

export type ResponseData = {
  type?: 'success' | 'error'
  data: { message: string; [key: string]: Any } | null
  response: Any
  /** Validation messages from a rejected submit, keyed by `param_key`. Only set when `type` is `'error'`. */
  fieldErrors?: FieldErrors
}

/**
 * Thin wrapper around `client.request` that handles content-type specific
 * parsing (CSV, PDF/blob), shows toasts on success/error and returns a
 * unified ResponseData shape.
 *
 * Example:
 *  const res = await dynamicClient('POST', `/users/${id}/reset_password`, { notify: true }, 'application/json')
 */
export const dynamicClient = async (
  method: FetchMethod = 'POST',
  path: string,
  data: unknown,
  response_content_type: Action['response_content_type'],
): Promise<ResponseData> => {
  const requestInit: Record<string, unknown> = { method, credentials: 'include' }
  if (method.toUpperCase() !== 'GET') {
    requestInit.data = data
  }

  const isPdf = response_content_type === 'application/pdf'
  const isCsv = response_content_type === 'text/csv'
  const shouldBlob = isPdf || path.endsWith('_generation')

  const parseAs = isCsv ? 'text' : shouldBlob ? 'blob' : 'json'
  requestInit.responseType = parseAs

  try {
    const res = await orvalFetch<{
      _data: Any
      response: Response
    }>(
      {
        url: path,
        method,
        ...requestInit,
      },
      {
        includeHeaders: true,
      },
    )

    const { response, _data: data, ...rest } = res

    const contentDisposition =
      response?.headers instanceof Headers
        ? response.headers.get('Content-Disposition') || response.headers.get('content-disposition')
        : ''

    if (isCsv && typeof data === 'string') {
      await handleCsvDownload(data, contentDisposition)

      return { data: { message: 'Download started.' }, response, type: 'success' }
    }

    if (isPdf || data instanceof Blob) {
      await handlePdfDownload(data as Blob, contentDisposition)

      return { data: { message: 'Download started.' }, response, type: 'success' }
    }

    emitToast({
      message: data?.message || 'Action completed successfully',
      type: 'success',
    })

    return { type: 'success', data, response, ...rest }
  } catch (error) {
    const fieldErrors = extractFieldErrors(error)
    const isValidationError = Object.keys(fieldErrors).length > 0

    if (!isValidationError) {
      console.error('DynamicClient error:', error)
    }

    return { type: 'error', data: null, response: null, fieldErrors }
  }
}
